import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { BrainCircuit, ThumbsUp, ThumbsDown, Sparkles, Pin, Plus, LogIn, X, Send } from 'lucide-react';
import { ContentModerationActions, SoftDeletePlaceholder } from './ModeratorControls';
import AuthCTA from './AuthCTA';
import EmptyState from './EmptyState';
import LiveSyncIndicator from './LiveSyncIndicator';
import InteractionTip from './InteractionTip';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { Link } from 'react-router-dom';
import { cn, normalizeSpeakers } from '../utils';
import { api } from '../api';
import usePolling from '../hooks/usePolling';
import useRateLimit from '../hooks/useRateLimit';
import ConfirmModal from './ConfirmModal';

import { useActivities } from '../context/ActivitiesContext';

import { useRoomTheme } from '../hooks/useRoomTheme';

const Brainstorming = ({ sessionState = 'live', isModerator = false, sessionId, communityId, roomData = null }) => {
  const { user } = useAuth();
  const { theme } = useRoomTheme(roomData?.settings?.room_type || 'Mixed');
  const sessionSpeakers = normalizeSpeakers(roomData?.speakers || roomData?.settings?.speakers || []);
  const { addToast } = useToast();
  const { rooms } = useActivities();
  const [ideas, setIdeas] = useState([]);
  const [newIdeaText, setNewIdeaText] = useState('');
  const [newIdeaCategory, setNewIdeaCategory] = useState('General');
  const [isClustering, setIsClustering] = useState(false);
  const { isSlowMode, timer, startCooldown } = useRateLimit(`brainstorm-${sessionId}`, 30);
  const [confirmConfig, setConfirmConfig] = useState({ isOpen: false });
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [lastSync, setLastSync] = useState(null);
  const [error, setError] = useState('');
  const timeoutRef = useRef({});

  useEffect(() => {
    return () => {
      Object.values(timeoutRef.current).forEach(clearTimeout);
    };
  }, []);

  const fetchIdeas = async () => {
    if (!sessionId) return;
    try {
      const endpoint = user ? `/ideas?room_id=${sessionId}` : `/public/ideas?room_id=${sessionId}`;
      const response = await api.get(endpoint);
      const data = response.data || response;
      const ideasArray = Array.isArray(data) ? data : (data.data || []);
      
      if (!Array.isArray(ideasArray)) {
        setIsInitialLoading(false);
        return;
      }

      setIdeas(prev => {
        const existingMap = new Map(prev.map(i => [i.id, i]));
        ideasArray.forEach(i => existingMap.set(i.id, i));
        return Array.from(existingMap.values());
      });
      setLastSync(new Date());
      setIsInitialLoading(false);
    } catch (err) {
      console.error('Failed to load ideas', err);
      throw err;
    }
  };

  const { isSyncing } = usePolling(fetchIdeas, 5000, [sessionId]);

  const handleInputChange = (e) => {
    setNewIdeaText(e.target.value);
  };

  const handleCluster = () => {
    setIsClustering(true);
    const tid = setTimeout(() => {
      setIsClustering(false);
      delete timeoutRef.current[tid];
    }, 2000);
    timeoutRef.current[tid] = tid;
  };

  const handleAddIdea = async () => {
    if ((sessionState !== 'live' && sessionState !== 'upcoming') || !newIdeaText.trim() || isSlowMode) {
      if (isSlowMode) {
        setError(`Slow mode active. Please wait ${timer}s.`);
        setTimeout(() => setError(''), 3000);
      }
      return;
    }

    const performSubmit = async () => {
      setError('');
      startCooldown();
      try {
        const endpoint = user ? '/ideas' : '/public/ideas';
        const created = await api.post(endpoint, {
          text: newIdeaText.trim(),
          category: newIdeaCategory.trim() || 'General',
          room_id: sessionId,
          community_id: communityId,
        });
        setIdeas((prev) => [created, ...prev]);
        setNewIdeaText('');
        setConfirmConfig({ isOpen: false });
      } catch (err) {
        console.error('Failed to post idea', err);
        setError(err.response?.data?.message || 'Unable to save the idea right now.');
      }
    };

    if (!user && ideas.length > 0) {
      setConfirmConfig({
        isOpen: true,
        title: 'Guest Contribution',
        description: 'You are contributing as a guest. Your ideas will be visible to everyone.',
        confirmText: 'Continue',
        type: 'info',
        onConfirm: performSubmit
      });
      return;
    }

    performSubmit();
  };

  const updateIdea = async (id, updates) => {
    if (sessionState === 'archived' || sessionState === 'locked') return;
    try {
      const updated = await api.put(`/ideas/${id}`, updates);
      setIdeas((prev) => Array.isArray(prev) ? prev.map((idea) => idea.id === updated.id ? updated : idea) : []);
    } catch (err) {
      console.error('Failed to update idea', err);
    }
  };

  const toggleDelete = (id, currentDeleted) => {
    updateIdea(id, { is_deleted: !currentDeleted });
  };

  const handleDeleteIdea = async (id) => {
    setConfirmConfig({
      isOpen: true,
      title: 'Delete Idea?',
      description: 'Are you sure you want to delete this idea? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await api.delete(`/ideas/${id}`);
          setIdeas((prev) => prev.filter(idea => idea.id !== id));
          setConfirmConfig({ isOpen: false });
        } catch (err) {
          console.error('Failed to delete idea', err);
        }
      }
    });
  };

  const togglePin = (id, currentHighlighted) => {
    updateIdea(id, { highlighted: !currentHighlighted });
  };

  const handleVote = async (id) => {
    if (sessionState !== 'live' && sessionState !== 'upcoming') return;
    
    // Check if voting is enabled in settings
    const votingEnabled = rooms.find(r => r.id === sessionId)?.settings?.voting !== false;
    if (!votingEnabled) {
      setError('Voting is disabled for this board.');
      const tid = setTimeout(() => {
        setError('');
        delete timeoutRef.current[tid];
      }, 3000);
      timeoutRef.current[tid] = tid;
      return;
    }

    try {
      const endpoint = user ? `/ideas/${id}/vote` : `/public/ideas/${id}/vote`;
      const updated = await api.post(endpoint);
      setIdeas((prev) => Array.isArray(prev) ? prev.map((idea) => idea.id === updated.id ? updated : idea) : []);
    } catch (err) {
      console.error('Idea vote failed', err);
    }
  };

  const handleDownvote = async (id) => {
    if (sessionState !== 'live') return;
    try {
      const endpoint = user ? `/ideas/${id}/downvote` : `/public/ideas/${id}/downvote`;
      const updated = await api.post(endpoint);
      setIdeas((prev) => Array.isArray(prev) ? prev.map((idea) => idea.id === updated.id ? updated : idea) : []);
    } catch (err) {
      console.error('Idea downvote failed', err);
    }
  };

  const isInteractionDisabled = sessionState !== 'live' && sessionState !== 'upcoming';
  const syncLabel = lastSync ? 'Live Sync active' : 'Connecting...';

  return (
    <div className="flex flex-col h-full bg-white rounded-3xl border border-gray-100 shadow-xl shadow-brand-indigo/5 overflow-hidden">
      <div className={cn("p-5 border-b border-gray-50 flex flex-col gap-4 md:flex-row items-start md:items-center justify-between bg-gradient-to-br to-white", theme.lightBg)}>
        <div className="flex items-center gap-3">
          <div className={cn("p-2 rounded-2xl", theme.lightBg, theme.text)}>
            <BrainCircuit size={20} className="fill-current opacity-20" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 leading-none">Brainstorming</h3>
            <div className="mt-1.5">
               <LiveSyncIndicator isSyncing={isSyncing} variant={theme.type?.toLowerCase() === 'mixed' ? 'orange' : theme.type?.toLowerCase() === 'poll' ? 'indigo' : theme.type?.toLowerCase() === 'brainstorm' ? 'green' : 'purple'} />
            </div>
          </div>
        </div>
         <div className="flex items-center gap-3">
             <span className={cn("bg-white text-[10px] font-bold px-3 py-1 rounded-full border shadow-sm uppercase tracking-widest", theme.text, theme.lightBorder)}>{ideas.length} Ideas</span>
         {isModerator && (
            <button 
               onClick={handleCluster}
               disabled={isClustering || isInteractionDisabled || ideas.length < 2}
               className={cn(
                  "flex items-center gap-2 text-white px-5 py-2.5 rounded-2xl text-[10px] font-bold uppercase tracking-widest transition-all shadow-lg",
                  theme.bg,
                  theme.shadow,
                  theme.hover,
                  (isClustering || isInteractionDisabled || ideas.length < 2) && "opacity-50 cursor-not-allowed shadow-none",
                  isClustering && "animate-pulse"
               )}
            >
               <Sparkles size={14} />
               {isClustering ? "AI Clustering..." : "Cluster Ideas"}
            </button>
         )}
        </div>
      </div>

      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        <div className={cn("p-4 bg-gradient-to-br to-white border-b border-gray-50", theme.lightBg)}>
          <InteractionTip message="Group thoughts by category for AI clustering" variant={theme.type?.toLowerCase() === 'mixed' ? 'orange' : theme.type?.toLowerCase() === 'poll' ? 'indigo' : theme.type?.toLowerCase() === 'brainstorm' ? 'green' : 'purple'} />
          <div className="grid gap-3">
            <div className="relative group">
              <input
                type="text"
                value={newIdeaText}
                onChange={handleInputChange}
                disabled={isInteractionDisabled || isSlowMode}
                onKeyDown={(e) => e.key === 'Enter' && handleAddIdea()}
                placeholder={
                  isSlowMode ? `Wait ${timer}s...` : 
                  (user ? "Share a suggestion..." : "Contribute anonymously...")
                }
                className={cn(
                  "w-full pl-5 pr-36 sm:pr-40 py-3.5 sm:py-4 rounded-2xl sm:rounded-3xl border-2 border-gray-50 bg-white text-sm font-bold text-gray-700 outline-none transition-all shadow-sm focus:shadow-xl focus:shadow-brand-indigo/5 placeholder:text-gray-300",
                  theme.ring,
                  `focus:${theme.border}`
                )}
              />
              <div className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 sm:gap-2">
                 <input
                  type="text"
                  value={newIdeaCategory}
                  onChange={(e) => setNewIdeaCategory(e.target.value)}
                  disabled={isInteractionDisabled || isSlowMode}
                  placeholder="Tag"
                  className={cn(
                    "w-16 sm:w-24 px-2 sm:px-3 py-1.5 sm:py-2 rounded-xl border bg-opacity-50 focus:bg-white text-[9px] sm:text-[10px] font-bold outline-none transition-all uppercase tracking-widest text-center placeholder:opacity-50",
                    theme.lightBg,
                    theme.text,
                    theme.lightBorder
                  )}
                />
                <button
                  onClick={handleAddIdea}
                  disabled={isInteractionDisabled || !newIdeaText.trim() || isSlowMode}
                  className={cn(
                    "p-2 sm:p-2.5 rounded-xl sm:rounded-2xl transition-all",
                    (isInteractionDisabled || !newIdeaText.trim() || isSlowMode) ? "bg-gray-100 text-gray-400" : cn("text-white shadow-lg", theme.bg, theme.shadow, theme.hover)
                  )}
                >
                  <Send size={16} className="sm:w-[18px] sm:h-[18px]" />
                </button>
              </div>
            </div>
          </div>
          {error && <p className="mt-4 text-[10px] font-bold text-red-500 uppercase tracking-widest px-2">{error}</p>}
        </div>

        <div className="flex-1 p-4 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-max">
          {ideas.length === 0 && !isInitialLoading && (
            <div className="col-span-full">
              <EmptyState 
                icon={BrainCircuit}
                title="No ideas yet"
                description="Be the first to share your thoughts and spark a new conversation!"
                variant={theme.type?.toLowerCase() === 'mixed' ? 'orange' : theme.type?.toLowerCase() === 'poll' ? 'indigo' : theme.type?.toLowerCase() === 'brainstorm' ? 'green' : 'purple'}
              />
            </div>
          )}

           {Array.isArray(ideas) && [...ideas]
            .sort((a, b) => {
              if (a.highlighted && !b.highlighted) return -1;
              if (!a.highlighted && b.highlighted) return 1;
              return new Date(b.created_at || Date.now()) - new Date(a.created_at || Date.now());
            })
            .map((idea) => (
            idea.is_deleted ? (
              <SoftDeletePlaceholder 
                key={idea.id} 
                type="idea" 
                onRestore={() => toggleDelete(idea.id, idea.is_deleted)} 
                sessionId={sessionId}
                communityId={communityId}
                session={roomData}
              />
            ) : (
              <motion.div 
                key={idea.id}
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className={cn(
                  "p-4 sm:p-5 rounded-[1.5rem] sm:rounded-[2rem] border transition-all flex flex-col justify-between relative group hover:scale-[1.01] min-w-0 break-words",
                  idea.highlighted ? cn("bg-white shadow-2xl z-10", theme.border, theme.shadow) : "border-gray-50 bg-gray-50/10 hover:bg-white hover:shadow-xl hover:shadow-brand-indigo/5"
                )}
              >
                {idea.highlighted && (
                  <div className={cn("absolute -top-1.5 -right-1.5 sm:-top-2 sm:-right-2 p-1.5 sm:p-2 text-white rounded-xl sm:rounded-2xl shadow-lg border-2 sm:border-4 border-white", theme.bg)}>
                    <Pin size={10} className="sm:w-3 sm:h-3 fill-white" />
                  </div>
                )}
                
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-2 sm:mb-3 overflow-hidden">
                     <span className={cn("text-[8px] sm:text-[10px] font-bold uppercase tracking-widest bg-opacity-50 px-2 sm:px-3 py-0.5 sm:py-1 rounded-full border truncate", theme.text, theme.lightBg, theme.lightBorder)}>
                       {idea.category || 'General'}
                    </span>
                  </div>
                  <p className="text-gray-900 font-bold text-xs sm:text-sm leading-relaxed whitespace-normal break-words">{idea.text}</p>
                </div>

                <div className="mt-4 sm:mt-5 pt-3 sm:pt-4 border-t border-gray-50 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <div className={cn("w-8 h-8 sm:w-10 sm:h-10 rounded-xl sm:rounded-2xl p-0.5 border overflow-hidden shadow-sm shrink-0", theme.lightBg, theme.lightBorder)}>
                      <img src={idea.author_user?.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${idea.author_user?.name || idea.author || 'guest'}`} alt="avatar" className="w-full h-full object-cover rounded-lg sm:rounded-xl" />
                    </div>
                    <div className="flex flex-col min-w-0">
                      <span className="text-[10px] sm:text-xs font-bold text-gray-900 truncate max-w-[100px] sm:max-w-[150px] leading-none mb-1">{idea.author_user?.name || idea.author || 'Guest'}</span>
                      <div className="flex flex-wrap gap-1">
                        {(String(idea.author_id) === String(roomData?.owner_id) || String(idea.author_id) === String(roomData?.host_id)) && (
                            <span className={cn("text-[7px] sm:text-[9px] font-bold uppercase tracking-widest", theme.text)}>Host</span>
                        )}
                        {sessionSpeakers.includes(idea.author_user?.name || idea.author) && (
                            <span className="text-[7px] sm:text-[9px] font-bold text-orange-600 uppercase tracking-widest">Speaker</span>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
                    <div className="flex opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                      <ContentModerationActions 
                        onDelete={() => String(idea.author_id) === String(user?.id) ? handleDeleteIdea(idea.id) : toggleDelete(idea.id, idea.is_deleted)} 
                        onPin={() => togglePin(idea.id, idea.highlighted)}
                        isPinned={idea.highlighted}
                        sessionId={sessionId}
                        communityId={communityId}
                        isAuthor={String(idea.author_id) === String(user?.id)}
                        session={roomData}
                        compact={true}
                      />
                    </div>
                    <div className={cn("flex items-center rounded-xl sm:rounded-2xl overflow-hidden border shadow-sm shrink-0", theme.lightBg, theme.lightBorder)}>
                      <button 
                        onClick={() => handleVote(idea.id)}
                        disabled={isInteractionDisabled}
                        className={cn(
                          "flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 sm:py-2 font-bold text-[10px] sm:text-xs transition-all border-r",
                          theme.lightBorder,
                          idea.user_has_voted ? cn("text-white shadow-lg", theme.bg, theme.shadow) : cn("hover:opacity-80", theme.text, theme.lightBg),
                          isInteractionDisabled && "cursor-not-allowed opacity-50"
                        )}
                      >
                        <ThumbsUp size={10} className={cn("sm:w-3 sm:h-3", idea.user_has_voted && "fill-white")} />
                        <span className="tabular-nums">{idea.votes || 0}</span>
                      </button>
                      <button 
                        onClick={() => handleDownvote(idea.id)}
                        disabled={isInteractionDisabled}
                        className={cn(
                          "flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 sm:py-2 font-bold text-[10px] sm:text-xs transition-all",
                          idea.user_has_downvoted ? "bg-red-500 text-white" : "text-gray-400 hover:bg-red-50 hover:text-red-500",
                          isInteractionDisabled && "cursor-not-allowed opacity-50"
                        )}
                      >
                        <ThumbsDown size={10} className={cn("sm:w-3 sm:h-3", idea.user_has_downvoted && "fill-white")} />
                        <span className="tabular-nums">{idea.downvotes || 0}</span>
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )
          ))}
        </div>
      </div>
    </div>

    {!user && (
        <div className={cn("p-4 border-t", theme.lightBg, theme.lightBorder)}>
          <AuthCTA message="Contribution is open to guests." variant={theme.type?.toLowerCase() === 'mixed' ? 'orange' : theme.type?.toLowerCase() === 'poll' ? 'indigo' : theme.type?.toLowerCase() === 'brainstorm' ? 'green' : 'purple'} />
        </div>
      )}

      <ConfirmModal 
        {...confirmConfig}
        onClose={() => setConfirmConfig({ ...confirmConfig, isOpen: false })}
      />
    </div>
  );
};

export default Brainstorming;
